import { Contact, ContactsApi, SuccessResponse } from '@citruscamps/citrus-client'
import { useQueryClient } from 'react-query'
import { useToast } from '../../../../../hooks/useToast'
import translations from '../../../../../translations/en.json'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateBaseKey } from '../../../../../utils/key-generator'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'
import { useFlags } from '../../../../../components/FeatureFlag/hooks/useFlags'
import { ROUTES } from '../../../../../constants/routes'
import { toQueryParams } from '../../../../../utils/query-params'
import { useRouter } from '../../../../../hooks/useRouter'

const convertToE164 = (phoneNumber: string) => {
  const digitsOnly = phoneNumber.replace(/\D/g, '')
  if (digitsOnly.startsWith('1')) {
    digitsOnly.substring(1)
  }
  return `+1${digitsOnly}`
}

interface IProps {
  programId?: string
}

interface IUpdateContacts {
  handleEmail: (values: Contact) => void
  handleTextMessage: (values: Contact) => void
  handleCall: (values: Contact) => void
  handleUpdate: (values: Contact) => Promise<void>
  handleArchive: (values: Contact) => Promise<void>
  handleInvite: (values: Contact) => Promise<void>
  handleUnarchive: (values: Contact) => Promise<void>
  handleSubscribe: (values: Contact) => Promise<void>
  handleUnsubscribe: (values: Contact) => Promise<void>
}

export const useUpdateContacts = ({ programId }: IProps): IUpdateContacts => {
  const { requestHandler } = useRequestHandler()
  const { flags } = useFlags()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { setToast } = useToast()

  const handleUpdate = async (contact: Contact) => {
    const contactClient = new ContactsApi(generateApiConfig())
    if (programId && contact?.id) {
      await requestHandler<Contact>(
        () =>
          contactClient.updateContact({
            program_id: programId,
            contact_id: contact.id,
            Contact: contact,
          }),
        {
          toast: {
            setToast,
          },
        },
      )
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'contact',
          program_id: programId,
        }),
      )
    } else {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleArchive = async (contact: Contact) => {
    const contactClient = new ContactsApi(generateApiConfig())
    if (programId && contact?.id) {
      await requestHandler<Contact>(
        () =>
          contactClient.updateContact({
            program_id: programId,
            contact_id: contact.id || '',
            Contact: {
              ...contact,
              is_archived: true,
            },
          }),
        {
          toast: {
            setToast,
          },
        },
      )
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'contact',
          program_id: programId,
        }),
      )
    } else {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleUnarchive = async (contact: Contact) => {
    const contactClient = new ContactsApi(generateApiConfig())
    if (programId && contact?.id) {
      await requestHandler<Contact>(
        () =>
          contactClient.updateContact({
            program_id: programId,
            contact_id: contact?.id || '',
            Contact: {
              ...contact,
              is_archived: false,
            },
          }),
        {
          toast: {
            setToast,
          },
        },
      )
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'contact',
          program_id: programId,
        }),
      )
    } else {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleInvite = async (contact: Contact) => {
    const contactClient = new ContactsApi(generateApiConfig())
    if (programId && contact?.id) {
      await requestHandler<SuccessResponse>(
        () => contactClient.inviteContact({ program_id: programId, contact_id: contact.id }),
        {
          toast: {
            setToast,
            success: (translations as any)['success.send_invite_message'],
          },
        },
      )
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'contact',
          program_id: programId,
        }),
      )
    } else {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleSubscribe = async (contact: Contact) => {
    const contactClient = new ContactsApi(generateApiConfig())
    if (programId && contact.id) {
      await requestHandler<Contact>(
        () =>
          contactClient.subscribeContactMarketing({
            program_id: programId,
            contact_id: contact.id,
          }),
        {
          toast: {
            setToast,
            success: (translations as any)['success.subscribe_contact'],
          },
        },
      )
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'contact',
          program_id: programId,
        }),
      )
    } else {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleUnsubscribe = async (contact: Contact) => {
    const contactClient = new ContactsApi(generateApiConfig())
    if (programId && contact.id) {
      await requestHandler<Contact>(
        () =>
          contactClient.unsubscribeContactMarketing({
            program_id: programId,
            contact_id: contact.id,
            ContactMarketingUnsubscribeParams: {
              id: contact.id,
              reason: 'unspecified',
            },
          }),
        {
          toast: {
            setToast,
            success: (translations as any)['success.unsubscribe_contact'],
          },
        },
      )
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'contact',
          program_id: programId,
        }),
      )
    } else {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleEmail = ({ id, email }: Pick<Contact, 'id' | 'email'>) => {
    if (!email) {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
      return
    }
    if (!!flags?.communications_enabled) {
      const search = toQueryParams({ contact_id: id, medium: 'email' })
      router.push({
        pathname: ROUTES.INBOX_CONVERSATION_COMPOSE,
        search,
      })
      return
    }
    var anchor = document.createElement('a')
    anchor.target = '_blank'
    anchor.href = `mailto:${email}`
    anchor.click()
  }

  const handleTextMessage = ({ id, phones = [] }: Pick<Contact, 'id' | 'phones'>) => {
    const [phone] = phones.map(convertToE164)
    if (!phone) {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
      return
    }
    if (!!flags?.sms_enabled) {
      const search = toQueryParams({ contact_id: id, medium: 'sms' })
      router.push({
        pathname: ROUTES.INBOX_CONVERSATION_COMPOSE,
        search,
      })
      return
    }
    var anchor = document.createElement('a')
    anchor.target = '_blank'
    anchor.href = `sms:${phone}`
    anchor.click()
  }

  const handleCall = ({ phones = [] }: Pick<Contact, 'phones'>) => {
    const [phone] = phones.map(convertToE164)
    if (!phone) {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
      return
    }
    var anchor = document.createElement('a')
    anchor.target = '_blank'
    anchor.href = `tel:${phone}`
    anchor.click()
  }

  return {
    handleEmail,
    handleTextMessage,
    handleCall,
    handleUpdate,
    handleArchive,
    handleInvite,
    handleUnarchive,
    handleSubscribe,
    handleUnsubscribe,
  }
}
